import React from 'react';
import {
  FaSync, FaEdit, FaTrash, FaExclamationTriangle, FaChartBar,
  FaTags, FaUsers, FaBullseye, FaSpinner, FaArrowUp, FaArrowDown,
  FaTable,
} from 'react-icons/fa';

import KpiWidget from './KpiWidget';
import ChartWidget from './ChartWidget';
import InsightWidget from './InsightWidget';
import RecommendationsWidget from './RecommendationsWidget';
import TableWidget from './TableWidget';

const TYPE_ICONS = {
  kpi: FaTags,
  chart: FaChartBar,
  insight: FaUsers,
  recommendations: FaBullseye,
  table: FaTable,
};

const WidgetCard = ({
  widget,
  editMode,
  isGenerating,
  canMoveUp = false,
  canMoveDown = false,
  onRefresh,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const { widget_type, status, data, title, size = 6 } = widget;
  const Icon = TYPE_ICONS[widget_type] || FaChartBar;

  // Clase CSS basada en el size (12 columnas)
  const colSpan = Math.max(1, Math.min(12, Number(size)));

  // Estado visual
  const showSpinner = isGenerating || status === 'generating';
  const showError = !showSpinner && status === 'error';
  const showEmpty = !showSpinner && status !== 'error' && (!data || Object.keys(data).length === 0);

  const renderBody = () => {
    if (showSpinner) {
      return (
        <div className="widget-body-loading">
          <FaSpinner className="widget-spinner spinning" />
          <span>Generando widget…</span>
          <small className="widget-loading-hint">Esto puede tardar unos segundos</small>
        </div>
      );
    }
    if (showError) {
      return (
        <div className="widget-body-error">
          <FaExclamationTriangle />
          <div>
            <strong>No pudimos generar este widget</strong>
            <p>Volvé a intentarlo o ajustá las instrucciones desde editar.</p>
          </div>
        </div>
      );
    }
    if (showEmpty) {
      return (
        <div className="widget-body-empty">
          <p>Este widget aún no tiene datos.</p>
          <button type="button" className="widget-empty-action" onClick={onRefresh}>
            <FaSync /> Generar ahora
          </button>
        </div>
      );
    }
    switch (widget_type) {
      case 'kpi':
        return <KpiWidget data={data} />;
      case 'chart':
        return <ChartWidget data={data} chartType={widget.chart_type} />;
      case 'insight':
        return <InsightWidget data={data} />;
      case 'recommendations':
        return <RecommendationsWidget data={data} />;
      case 'table':
        return <TableWidget data={data} />;
      default:
        return <div className="widget-body-empty">Tipo de widget desconocido: {widget_type}</div>;
    }
  };

  return (
    <div
      className={`widget-card widget-type-${widget_type} widget-col-${colSpan} ${editMode ? 'edit-mode' : ''}`}
    >
      <div className="widget-card-header">
        <div className="widget-card-header-left">
          <Icon className="widget-card-icon" />
          <span className="widget-card-title">{title || 'Widget'}</span>
        </div>
        <div className="widget-card-actions">
          {editMode && (
            <>
              <button
                type="button"
                className="widget-action-btn"
                onClick={onMoveUp}
                disabled={!canMoveUp}
                title="Mover antes"
              >
                <FaArrowUp />
              </button>
              <button
                type="button"
                className="widget-action-btn"
                onClick={onMoveDown}
                disabled={!canMoveDown}
                title="Mover después"
              >
                <FaArrowDown />
              </button>
              <span className="widget-action-divider" aria-hidden="true" />
            </>
          )}
          <button
            type="button"
            className="widget-action-btn"
            onClick={onRefresh}
            disabled={showSpinner}
            title="Actualizar"
          >
            <FaSync className={showSpinner ? 'spinning' : ''} />
          </button>
          {editMode && (
            <>
              <button
                type="button"
                className="widget-action-btn"
                onClick={onEdit}
                title="Editar"
              >
                <FaEdit />
              </button>
              <button
                type="button"
                className="widget-action-btn widget-action-danger"
                onClick={onDelete}
                title="Eliminar"
              >
                <FaTrash />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="widget-card-content">
        {renderBody()}
      </div>
    </div>
  );
};

export default WidgetCard;
